import { useEffect, useRef, useEffectEvent, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { OrderFormService } from "@domain/services/orderForm.service";

const PAGES_TO_CLEAR_STATE = [
  "/map",
  "/menu",
  "/profile",
  "/order/history",
  "/payment/settings",
  "/support",
  "/faq",
  "/discounts"
];

const ORDER_FLOW_PAGES = [
  "/catalog",
  "/service",
  "/docview",
  "/cart",
  "/payment",
  "/payment/status"
];

function shouldClearState(
  currentPath: string,
  previousPath: string | null,
  orderState: ReturnType<typeof OrderFormService.getOrder>
): boolean {
  if (!PAGES_TO_CLEAR_STATE.includes(currentPath)) {
    return false;
  }

  const wasOnOrderFlow = previousPath !== null && ORDER_FLOW_PAGES.includes(previousPath);
  const hasDataToClear =
    orderState.cart.services.length > 0 || Boolean(orderState.editing.service);

  return wasOnOrderFlow || hasDataToClear;
}

export function useOrderFormState(machineId: number) {
  // Мемоизируем orderState, чтобы избежать множественных вызовов getOrder
  const orderState = useMemo(() => {
    return OrderFormService.getOrder(machineId);
  }, [machineId]);
  
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);

  const handlePathChange = useEffectEvent(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    previousPathRef.current = currentPath;

    if (shouldClearState(currentPath, previousPath, orderState)) {
      orderState.clearState();
    }
  });

  useEffect(() => {
    handlePathChange();
  }, [location.pathname, handlePathChange]);

  return orderState;
}
